import { Button, Throbber, TourQuantity } from '@lt/components';
import cn from 'classnames';
import React from 'react';

import { FlightWithMoon, Moon } from '@lt/components/icons-react/userflow';
import { useTranslation } from '@lt/localization';
import { FLIGHT_TYPES_NAME } from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import styled from 'styled-components';
import type { ExtraItem } from '../../../../../../types/extras';
import type {
  FlightsData,
  TransformedBackFlight,
} from '../../../../types/flights';
import { findFlight } from '../../../../utils/flightUtils';
import FlightSummary from '../../../../../../components/PriceSummary/FlightSummary';
import FlightChangeWarning from '../../FlightChangeWarning';
import FlightsLabelList from '../../FlightsLabelList';
import FlightsRoute from '../../FlightsRoute';
import LabelsContainer from '../../LabelsContainer';
import FlightsLuggageSummary from '../../Luggage/FlightsLuggageSummary';
import SkeletonDetails from '../../SkeletonDetails/SkeletonDetails';
import { unknownLuggage } from '../../constants';
import CheapestFlightLabel from '../../CheapestFlightLabel';

export const baseCls = 'checkout-flight';
export const detailsCls = `${baseCls}-details`;

export interface FlightsItemProps {
  active?: boolean;
  className: string;
  buttonText?: string;
  data: FlightsData;
  couldBeChanged?: boolean;
  extras: ExtraItem[];
  packagePrice: number;
  instantConfirm?: boolean;
  onSelect: (arg: {
    flight_pair_id: TransformedBackFlight['flight_pair_id'];
    extras: ExtraItem[];
    tour_id: TransformedBackFlight['tour_id'];
  }) => void;
  selectedBackId: string;
  showFuelChargeSeparator?: boolean;
  isLoading: boolean;
  flightNumberTo: string;
  flightNumberBack: string;
  clientCashbackDiscount: number;
  transferAndMedInsurance: {
    insurance: boolean;
    transfer: boolean;
    airports: {
      id: number;
      name: string;
      distance: number;
      transferTime: number;
    };
    transferPrivate: boolean;
  };
  nights: number;
  nightsInHotel: number;
  hasDirectCheapestLabel: boolean;
  hasCheapestLabel: boolean;
}

const FlightsItem = ({
  data: { to, back },
  extras,
  onSelect,
  selectedBackId,
  packagePrice,
  couldBeChanged,
  buttonText,
  showFuelChargeSeparator,
  instantConfirm,
  active,
  isLoading,
  clientCashbackDiscount,
  transferAndMedInsurance,
  nights,
  nightsInHotel,
  flightNumberBack,
  flightNumberTo,
  className,
  hasCheapestLabel,
  hasDirectCheapestLabel,
}: FlightsItemProps) => {
  const { t } = useTranslation();
  const selectedBack = Array.isArray(back)
    ? findFlight(back, selectedBackId) || back[0] // Ищем самый дешевый
    : back;
  const { flight_pair_id, fuel_charge, availability, tour_id } = selectedBack;
  const detailsBlockCls = cn(`${detailsCls} ${baseCls}__details`);
  const summaryLuggage = couldBeChanged
    ? [unknownLuggage, unknownLuggage]
    : [to.luggage, selectedBack.luggage];
  const { insurance, transfer, airports, transferPrivate } =
    transferAndMedInsurance;

  const additionalNightsInFlight = nights !== nightsInHotel;
  // LT-38561: Удалить запись replay session на чекауте (https://tracker.yandex.ru/LT-38561)
  // Инициируем кастомное событие для отправки сессии в сентри
  const onFlightSelect = new CustomEvent('flightSelect');

  return (
    <div className={className}>
      {' '}
      <div
        className={cn(baseCls, 'checkout-flights__item', {
          [`${baseCls}__active`]: active,
        })}
      >
        <div className={`${baseCls}__cutout`} />

        {hasCheapestLabel && <CheapestFlightLabel direct={false} />}
        {hasDirectCheapestLabel && <CheapestFlightLabel direct />}

        <div className={detailsBlockCls}>
          {couldBeChanged ? (
            <>
              <FlightChangeWarning />
              <SkeletonDetails
                isMobile={false}
                to={{
                  departure: to.departure,
                  arrival: to.arrival,
                  departureCity: to.origin.city.name,
                  arrivalCity: to.destination.city.name,
                  flightType: FLIGHT_TYPES_NAME.CHARTER,
                }}
                back={{
                  departure: selectedBack.departure,
                  arrival: selectedBack.arrival,
                  departureCity: selectedBack.origin.city.name,
                  arrivalCity: selectedBack.destination.city.name,
                  flightType: FLIGHT_TYPES_NAME.CHARTER,
                }}
              />
            </>
          ) : (
            <>
              {' '}
              <div className={`${detailsCls}__item`}>
                <div className={`${detailsCls}__item-label`}>
                  {t('checkout.flight.item.to')}
                </div>
                <FlightsRoute data={to} type="to" showLogo />
                <LabelsContainer
                  data={to}
                  type="to"
                  flightNumber={flightNumberTo}
                  isBusinessClass={to.business}
                />
              </div>
              <div className={`${baseCls}__fuel-separator`}>
                <div className={`${baseCls}__fuel-icon`}>
                  <StyledFlightWithMoon height={16} width={16} />
                </div>
                <span className={`${baseCls}__fuel-text like-h4`}>
                  {t('checkout.flight.separator.withTheseFlights')}{' '}
                  {nightsInHotel}
                  <StyledMoon /> {t('checkout.flight.separator.inHotel')}{' '}
                  {additionalNightsInFlight && (
                    <span>
                      ({nights}
                      <StyledMoon /> {t('checkout.flight.separator.withFlight')}
                      )
                    </span>
                  )}
                </span>
                <div className={`${baseCls}__separator`} />
              </div>
              <div
                className={cn(`${detailsCls}__item`, {
                  [`${detailsCls}__item_single`]: showFuelChargeSeparator,
                })}
              >
                <div
                  className={`${detailsCls}__item-label ${detailsCls}__item-label_back`}
                >
                  {t('checkout.flight.item.back')}
                </div>
                <FlightsRoute type="back" data={selectedBack} showLogo />
                <LabelsContainer
                  type="back"
                  data={selectedBack}
                  flightNumber={flightNumberBack}
                  isBusinessClass={selectedBack.business}
                />
              </div>
            </>
          )}
        </div>

        <div className={`${baseCls}__details-right`}>
          <FlightsLabelList tourId={tour_id} />
          <FlightsLuggageSummary summaryLuggage={summaryLuggage} />

          <div className={`${baseCls}__summary`}>
            <FlightSummary
              packagePrice={packagePrice}
              clientCashbackDiscount={clientCashbackDiscount}
              fuelCharge={fuel_charge}
              extras={extras}
              instantConfirm={instantConfirm}
              tourId={tour_id}
              insurance={insurance}
              transfer={transfer}
              airports={airports}
              transferPrivate={transferPrivate}
            />
            <Button
              primary
              className={`${baseCls}__submit`}
              onClick={() => {
                onSelect({ flight_pair_id, extras, tour_id });
                // LT-38561: Удалить запись replay session на чекауте (https://tracker.yandex.ru/LT-38561)
                document.dispatchEvent(onFlightSelect);
              }}
              data-at-package-select-flight
              size="large"
            >
              {isLoading ? (
                <Throbber embed size={30} color="#444" />
              ) : (
                buttonText
              )}
            </Button>

            <TourQuantity
              position="center"
              text={availability && availability.tooltip}
              withTooltip={false}
              availability={availability}
              marginTop={3}
              marginBottom={3}
              maxWidth="160px"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledFlightWithMoon = styled(FlightWithMoon)`
  height: 16px;
  width: 16px;
  stroke: black;
`;

const StyledMoon = styled(Moon)`
  margin-left: 2px;
  display: inline;
  stroke: black;
  stroke-width: 0.1;
  width: 12px;
  height: 12px;

  path {
    fill: black;
  }
`;

export default FlightsItem;
